// Local crash recovery for the compose sheet: the subject and body being typed are kept in this
// browser's localStorage per draft (or per new message and thread before the first save) until
// the server has them. After a crash or a reload the sheet offers "Restore your unsaved text?"
// once. A successful save or send clears the copy; signing out clears every copy, and copies
// past their age are swept when the app starts. Storage can be missing, full or blocked (private
// windows, site data off): every access is wrapped and failure only means no recovery.

export interface RecoveredText {
  subject: string;
  bodyHtml: string;
  /** When it was kept (epoch ms). */
  savedAt: number;
}

/** Kept copies older than this are not offered (and are dropped when read). */
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;
const PREFIX = "meemail:compose:v1:";

export interface RecoveryContext {
  draftId?: string | null | undefined;
  accountId?: string | null | undefined;
  replyToThreadKey?: string | null | undefined;
}

/** The key of a draft's copy; before the first save, of a new message in this account and thread. */
export function recoveryKey(ctx: RecoveryContext): string | null {
  if (ctx.draftId) return `${PREFIX}draft:${ctx.draftId}`;
  if (!ctx.accountId) return null;
  return `${PREFIX}new:${ctx.accountId}:${ctx.replyToThreadKey ?? ""}`;
}

/** localStorage when the browser allows it, else null. */
export function browserStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function writeRecovery(
  storage: Storage | null,
  key: string | null,
  text: Omit<RecoveredText, "savedAt">,
  now = Date.now(),
): void {
  if (!storage || !key) return;
  try {
    storage.setItem(key, JSON.stringify({ ...text, savedAt: now }));
  } catch {
    // Full or blocked: the server autosave is still running.
  }
}

export function clearRecovery(storage: Storage | null, key: string | null): void {
  if (!storage || !key) return;
  try {
    storage.removeItem(key);
  } catch {
    // Nothing to do.
  }
}

function isRecovered(value: unknown): value is RecoveredText {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.subject === "string" &&
    typeof v.bodyHtml === "string" &&
    typeof v.savedAt === "number" &&
    Number.isFinite(v.savedAt)
  );
}

/** The kept copy, if there is a readable, recent one. Stale or unreadable copies are dropped. */
export function readRecovery(
  storage: Storage | null,
  key: string | null,
  now = Date.now(),
): RecoveredText | null {
  if (!storage || !key) return null;
  let raw: string | null;
  try {
    raw = storage.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearRecovery(storage, key);
    return null;
  }
  if (!isRecovered(parsed) || parsed.savedAt > now + 60_000 || now - parsed.savedAt > MAX_AGE_MS) {
    clearRecovery(storage, key);
    return null;
  }
  return parsed;
}

/**
 * What to offer when the sheet opens: the kept copy when it differs from what the server has
 * (and has something in it); null when there is nothing to restore.
 */
export function recoveryOffer(
  kept: RecoveredText | null,
  server: { subject: string; bodyHtml: string },
): RecoveredText | null {
  if (!kept) return null;
  const empty = !kept.subject.trim() && !kept.bodyHtml.replace(/<[^>]*>/g, "").trim();
  if (empty) return null;
  return kept.subject === server.subject && kept.bodyHtml === server.bodyHtml ? null : kept;
}

function recoveryKeys(storage: Storage): string[] {
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key?.startsWith(PREFIX)) keys.push(key);
  }
  return keys;
}

/** Signing out: no copy of what was typed stays in this browser. */
export function clearAllRecovery(storage: Storage | null): void {
  if (!storage) return;
  try {
    for (const key of recoveryKeys(storage)) clearRecovery(storage, key);
  } catch {
    // Blocked storage holds nothing of ours.
  }
}

/** On start: drops copies past their age (or unreadable), so old text does not linger. */
export function sweepRecovery(storage: Storage | null, now = Date.now()): void {
  if (!storage) return;
  try {
    for (const key of recoveryKeys(storage)) readRecovery(storage, key, now);
  } catch {
    // Nothing to sweep.
  }
}
